/**
 * Notification Context
 * Manages push notifications and in-app notification state
 * Supports browser push notifications and real-time alerts
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import logger from '../utils/logger';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pushPermission, setPushPermission] = useState('default');
  const [pendingApprovals, setPendingApprovals] = useState([]);

  // Check push notification permission on mount
  useEffect(() => {
    if ('Notification' in window) {
      setPushPermission(Notification.permission);
    }
    
    // Load pending approvals from mock data
    loadPendingApprovals();
    
    // Simulate real-time notifications
    const interval = setInterval(() => {
      checkForNewNotifications();
    }, 30000); // Check every 30 seconds
    
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update unread count when notifications change
  useEffect(() => {
    const unread = notifications.filter(n => !n.read).length;
    setUnreadCount(unread);
  }, [notifications]);

  // Request push notification permission
  const requestPushPermission = async () => {
    if (!('Notification' in window)) {
      
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      setPushPermission(permission);
      return permission === 'granted';
    } catch (error) {
      logger.error('Error requesting notification permission:', error);
      return false;
    }
  };

  // Send browser push notification
  const sendPushNotification = useCallback((title, options = {}) => {
    if (pushPermission !== 'granted') return;
    
    try {
      const notification = new Notification(title, {
        icon: '/logo192.png',
        badge: '/logo192.png',
        tag: options.tag || 'default',
        requireInteraction: options.requireInteraction || false,
        ...options
      });

      notification.onclick = () => {
        window.focus();
        if (options.onClick) options.onClick();
        notification.close();
      };

      return notification;
    } catch (error) {
      logger.error('Error sending push notification:', error);
    }
  }, [pushPermission]);

  // Add in-app notification
  const addNotification = useCallback((notification) => {
    const newNotification = {
      id: Date.now().toString(),
      timestamp: new Date().toISOString(),
      read: false,
      ...notification
    };

    setNotifications(prev => [newNotification, ...prev].slice(0, 50)); // Keep last 50

    // Also send push notification if permission granted and document is hidden
    if (document.hidden && notification.push !== false) {
      sendPushNotification(notification.title, {
        body: notification.message,
        tag: notification.type,
        requireInteraction: notification.urgent
      });
    }

    return newNotification;
  }, [sendPushNotification]);

  // Mark notification as read
  const markAsRead = useCallback((notificationId) => {
    setNotifications(prev =>
      prev.map(n =>
        n.id === notificationId ? { ...n, read: true } : n
      )
    );
  }, []);

  // Mark all as read
  const markAllAsRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }, []);

  // Clear notification
  const clearNotification = useCallback((notificationId) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  }, []);

  // Load pending approvals (mock data)
  const loadPendingApprovals = () => {
    const mockApprovals = [
      {
        id: 'apr-001',
        type: 'document',
        title: 'RFP for IT Infrastructure Services',
        description: 'Requires CO signature before posting to SAM.gov',
        requestedBy: 'Sarah Johnson',
        requestedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
        priority: 'high',
        deadline: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        procurementId: 'VA-2026-00123',
        status: 'pending'
      },
      {
        id: 'apr-002',
        type: 'vendor_qa',
        title: 'Vendor Question Response',
        description: 'Response to TechCorp question about security requirements',
        requestedBy: 'Mike Chen',
        requestedAt: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
        priority: 'medium',
        deadline: new Date(Date.now() + 48 * 60 * 60 * 1000).toISOString(),
        procurementId: 'VA-2026-00089',
        status: 'pending'
      },
      {
        id: 'apr-003',
        type: 'modification',
        title: 'Contract Modification #3',
        description: 'Scope change request for Cloud Migration project',
        requestedBy: 'Lisa Park',
        requestedAt: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
        priority: 'high',
        deadline: new Date(Date.now() + 12 * 60 * 60 * 1000).toISOString(),
        procurementId: 'VA-2025-00456',
        status: 'pending'
      },
      {
        id: 'apr-004',
        type: 'evaluation',
        title: 'Technical Evaluation Complete',
        description: 'TEB scoring ready for CO review - Cybersecurity Assessment',
        requestedBy: 'David Kim',
        requestedAt: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
        priority: 'medium',
        deadline: new Date(Date.now() + 72 * 60 * 60 * 1000).toISOString(),
        procurementId: 'VA-2026-00201',
        status: 'pending'
      },
      {
        id: 'apr-005',
        type: 'j_and_a',
        title: 'J&A Approval Required',
        description: 'Sole source justification for emergency IT repair',
        requestedBy: 'Anna Martinez',
        requestedAt: new Date(Date.now() - 15 * 60 * 1000).toISOString(),
        priority: 'critical',
        deadline: new Date(Date.now() + 4 * 60 * 60 * 1000).toISOString(),
        procurementId: 'VA-2026-00234',
        status: 'pending'
      }
    ];
    setPendingApprovals(mockApprovals);
  };

  // Approve item
  const approveItem = useCallback(async (approvalId, comments = '') => {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 500));
    
    setPendingApprovals(prev =>
      prev.map(a =>
        a.id === approvalId
          ? { ...a, status: 'approved', approvedAt: new Date().toISOString(), comments }
          : a
      )
    );

    addNotification({
      type: 'success',
      title: 'Item Approved',
      message: 'Your approval has been recorded',
      push: false
    });

    return true;
  }, [addNotification]);

  // Reject item
  const rejectItem = useCallback(async (approvalId, reason) => {
    if (!reason) {
      throw new Error('Rejection reason is required');
    }

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 500));
    
    setPendingApprovals(prev =>
      prev.map(a =>
        a.id === approvalId
          ? { ...a, status: 'rejected', rejectedAt: new Date().toISOString(), rejectionReason: reason }
          : a
      )
    );

    addNotification({
      type: 'warning',
      title: 'Item Rejected',
      message: 'Item has been returned with feedback',
      push: false
    });

    return true;
  }, [addNotification]);

  // Check for new notifications (simulated)
  const checkForNewNotifications = useCallback(() => {
    // Random chance to add a new notification for demo purposes
    if (Math.random() < 0.1) {
      const types = ['deadline', 'approval', 'update', 'alert'];
      const type = types[Math.floor(Math.random() * types.length)];
      
      const messages = {
        deadline: { title: 'Deadline Approaching', message: 'RFP response deadline in 2 hours' },
        approval: { title: 'New Approval Request', message: 'Document requires your signature' },
        update: { title: 'Status Update', message: 'Vendor submitted response to your solicitation' },
        alert: { title: 'System Alert', message: 'New compliance update available' }
      };

      addNotification({
        type,
        ...messages[type],
        urgent: type === 'deadline'
      });
    }
  }, [addNotification]);

  // Get pending count by priority
  const getPendingByPriority = useCallback(() => {
    const pending = pendingApprovals.filter(a => a.status === 'pending');
    return {
      critical: pending.filter(a => a.priority === 'critical').length,
      high: pending.filter(a => a.priority === 'high').length,
      medium: pending.filter(a => a.priority === 'medium').length,
      low: pending.filter(a => a.priority === 'low').length,
      total: pending.length
    };
  }, [pendingApprovals]);

  const value = {
    notifications,
    unreadCount,
    pendingApprovals: pendingApprovals.filter(a => a.status === 'pending'),
    allApprovals: pendingApprovals,
    pushPermission,
    requestPushPermission,
    sendPushNotification,
    addNotification,
    markAsRead,
    markAllAsRead,
    clearNotification,
    approveItem,
    rejectItem,
    getPendingByPriority
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
